import React from "react";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  type DataExportTarget,
  type SalesMetricsExportAggregation,
} from "@features/data-export/types";
import { Checkbox } from "@shared/checkbox";
import { Radio } from "@shared/radio-group";

interface Props {
  target: DataExportTarget;
  exportAggregation: SalesMetricsExportAggregation;
  removeZeroSpend: boolean;
  aggregateData: boolean;
  includeIds: boolean;
  includeKindAndPlatform: boolean;
  onExportAggregationChange(val: SalesMetricsExportAggregation): void;
  onRemoveZeroSpendChange(val: boolean): void;
  onAggregateDataChange(val: boolean): void;
  onIncludeIdsChange(val: boolean): void;
  onIncludeKindAndPlatformChange(val: boolean): void;
}

export const ExtraOptionsSection = ({
  target,
  exportAggregation,
  removeZeroSpend,
  aggregateData,
  includeIds,
  includeKindAndPlatform,
  onExportAggregationChange,
  onRemoveZeroSpendChange,
  onAggregateDataChange,
  onIncludeIdsChange,
  onIncludeKindAndPlatformChange,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();

  return (
    <div>
      <p className="text-xs text-zinc-500 font-light mb-5">
        Select from any of the following extra options:
      </p>
      <div className="flex flex-wrap text-[12px] font-light gap-y-4 mb-4">
        {target === "performance-metrics" ? (
          <>
            <div className="flex-1 min-w-[13rem]">
              <Radio
                label="Export data broken down by label"
                value="breakdown"
                checked={exportAggregation === "breakdown"}
                onChange={(val) => {
                  prettyLogEventPage({
                    domain: "DataExport",
                    action: "onSelect",
                    additionalActionInfo: "Export aggregation changed",
                    data: {
                      newExportAggregation: val,
                      pageContext: { exportAggregation },
                    },
                  });

                  onExportAggregationChange(val);
                }}
                labelClassName="cursor-pointer"
                inputClassName="cursor-pointer h-3 w-3"
              />
            </div>
            <div className="flex-1 min-w-[13rem]">
              <Radio
                label="Export data broken down by date"
                value="date"
                checked={exportAggregation === "date"}
                onChange={(val) => {
                  prettyLogEventPage({
                    domain: "DataExport",
                    action: "onSelect",
                    additionalActionInfo: "Export aggregation changed",
                    data: {
                      newExportAggregation: val,
                      pageContext: { exportAggregation },
                    },
                  });

                  onExportAggregationChange(val);
                }}
                labelClassName="cursor-pointer"
                inputClassName="cursor-pointer h-3 w-3"
              />
            </div>
          </>
        ) : (
          <div className="flex-1 min-w-[13rem]">
            <Checkbox
              label="Export data broken down by date"
              inputClassName="w-3 h-3 mr-0.5"
              value={exportAggregation === "date"}
              onChange={(val) => {
                prettyLogEventPage({
                  domain: "DataExport",
                  action: "onSelect",
                  additionalActionInfo: "Export aggregation changed",
                  data: {
                    newExportAggregation: val,
                    pageContext: { exportAggregation },
                  },
                });

                onExportAggregationChange(val ? "date" : "breakdown");
              }}
            />
          </div>
        )}
      </div>
      <div className="text-[12px] font-light mb-4">
        <Checkbox
          label="Remove $0 spend / 0 transactions items"
          inputClassName="w-3 h-3 mr-0.5"
          value={removeZeroSpend}
          onChange={(val) => {
            prettyLogEventPage({
              domain: "DataExport",
              action: "onSelect",
              additionalActionInfo: "Remove 0 spend changed",
              data: {
                newRemoveZeroSpend: val,
                pageContext: { removeZeroSpend },
              },
            });

            onRemoveZeroSpendChange(val);
          }}
        />
      </div>
      <div className="text-[12px] font-light mb-4">
        <Checkbox
          label="Export aggregated data only"
          inputClassName="w-3 h-3 mr-0.5"
          value={aggregateData}
          onChange={(val) => {
            prettyLogEventPage({
              domain: "DataExport",
              action: "onSelect",
              additionalActionInfo: "Export aggregated data only",
              data: {
                newAggregateData: val,
                pageContext: { aggregateData },
              },
            });

            onAggregateDataChange(val);
          }}
        />
      </div>
      <div className="text-[12px] font-light mb-4">
        <Checkbox
          label="Include IDs"
          inputClassName="w-3 h-3 mr-0.5"
          value={includeIds}
          onChange={(val) => {
            prettyLogEventPage({
              domain: "DataExport",
              action: "onSelect",
              additionalActionInfo: "Include ids",
              data: {
                newIncludeIds: val,
                pageContext: { includeIds },
              },
            });

            onIncludeIdsChange(val);
          }}
        />
      </div>
      <div className="text-[12px] font-light mb-4">
        <Checkbox
          label="Include Kind And Platform"
          inputClassName="w-3 h-3 mr-0.5"
          value={includeKindAndPlatform}
          onChange={(val) => {
            prettyLogEventPage({
              domain: "DataExport",
              action: "onSelect",
              additionalActionInfo: "Include kind and platform",
              data: {
                newIncludeKindAndPlatform: val,
                pageContext: { includeKindAndPlatform },
              },
            });

            onIncludeKindAndPlatformChange(val);
          }}
        />
      </div>
    </div>
  );
};
